import React, { useState, useEffect } from 'react'
import { ScheduleWrapper, ScheduleHeader, CalendarContainer, ScheduleList, ScheduleItem, AddButton, ModalContent } from './styles'
import { Calendar, Modal, Form, Input, DatePicker, ColorPicker, Button, message } from 'antd'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/ko'
import locale from 'antd/locale/ko_KR'
import { useSchedule } from 'app/gomsacademy/hooks/Schedule'
import API from 'utils/api'
import { useToken } from 'app/hooks'
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa'
import { ConfigProvider } from 'antd'

dayjs.locale('ko')

interface Schedule {
	id: number
	title: string
	content?: string
	startDate: string
	endDate: string
	color?: string
	userName?: string
}

export const Schedule = () => {
	const { data: token } = useToken()
	const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs())
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isEditMode, setIsEditMode] = useState(false)
	const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null)
	const [selectedColor, setSelectedColor] = useState<string>('#3788d8')
	const [lastClick, setLastClick] = useState<{ date: string; ts: number } | null>(null)
	const [form] = Form.useForm()

	// Get start and end of current month
	const monthStart = selectedDate.startOf('month').format('YYYY-MM-DD')
	const monthEnd = selectedDate.endOf('month').format('YYYY-MM-DD')

	const { ScheduleData, mutate } = useSchedule(monthStart, monthEnd)

	const openAddScheduleForDate = (date: Dayjs) => {
		setIsEditMode(false)
		setEditingSchedule(null)
		setSelectedColor('#3788d8')
		form.resetFields()
		form.setFieldsValue({
			title: '',
			content: '',
			startDate: date.startOf('day'),
			endDate: date.endOf('day'),
		})
		setIsModalOpen(true)
	}

	const handleDateSelect = (date: Dayjs) => {
		setSelectedDate(date)

		// Double-click on same date to open "add schedule" modal prefilled for that date
		const now = Date.now()
		const dateKey = date.format('YYYY-MM-DD')
		if (lastClick && lastClick.date === dateKey && now - lastClick.ts < 450) {
			openAddScheduleForDate(date)
			setLastClick(null)
			return
		}
		setLastClick({ date: dateKey, ts: now })
	}

	const handleAddSchedule = () => {
		openAddScheduleForDate(selectedDate)
	}

	const handleEditSchedule = (schedule: Schedule) => {
		setIsEditMode(true)
		setEditingSchedule(schedule)
		setSelectedColor(schedule.color || '#3788d8')
		form.setFieldsValue({
			title: schedule.title,
			content: schedule.content || '',
			startDate: dayjs(schedule.startDate),
			endDate: dayjs(schedule.endDate),
		})
		setIsModalOpen(true)
	}

	const handleDeleteSchedule = async (scheduleId: number) => {
		if (window.confirm('정말로 이 일정을 삭제하시겠습니까?')) {
			try {
				const res = await API.delete(`/schedule/${scheduleId}`, token + '', {})
				if (res.result?.success) {
					message.success('일정이 삭제되었습니다')
					mutate()
				} else {
					message.error('일정 삭제에 실패했습니다')
				}
			} catch (err: any) {
				message.error(err?.message || '일정 삭제 중 오류가 발생했습니다')
			}
		}
	}

	const handleDeleteFromModal = async () => {
		if (!editingSchedule?.id) return
		if (window.confirm('정말로 이 일정을 삭제하시겠습니까?')) {
			try {
				const res = await API.delete(`/schedule/${editingSchedule.id}`, token + '', {})
				if (res.result?.success) {
					message.success('일정이 삭제되었습니다')
					setIsModalOpen(false)
					setEditingSchedule(null)
					setIsEditMode(false)
					mutate()
				} else {
					message.error('일정 삭제에 실패했습니다')
				}
			} catch (err: any) {
				message.error(err?.message || '일정 삭제 중 오류가 발생했습니다')
			}
		}
	}

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields()

			const start = values.startDate ? dayjs(values.startDate).startOf('day') : null
			if (!start) {
				message.error('시작일을 선택해주세요')
				return
			}

			// If endDate is missing, auto-fill to end of start day
			const end = values.endDate ? dayjs(values.endDate).endOf('day') : start.endOf('day')

			const scheduleData = {
				title: values.title,
				content: values.content || '',
				startDate: start.format('YYYY-MM-DDTHH:mm:ss'),
				endDate: end.format('YYYY-MM-DDTHH:mm:ss'),
				color: selectedColor || '#3788d8',
			}

			if (isEditMode && editingSchedule) {
				// Update schedule
				const res = await API.put(`/schedule/${editingSchedule.id}`, token + '', scheduleData)
				if (res.result?.success) {
					message.success('일정이 수정되었습니다')
					setIsModalOpen(false)
					mutate()
				} else {
					message.error('일정 수정에 실패했습니다')
				}
			} else {
				// Create schedule
				const res = await API.post('/schedule', token + '', scheduleData)
				if (res.result?.success || res.result?.id) {
					message.success('일정이 생성되었습니다')
					setIsModalOpen(false)
					mutate()
				} else {
					message.error('일정 생성에 실패했습니다')
				}
			}
		} catch (err: any) {
			if (err?.errorFields) {
				// Form validation errors
				return
			}
			message.error(err?.message || '일정 저장 중 오류가 발생했습니다')
		}
	}

	const dateCellRender = (date: Dayjs) => {
		const dateStr = date.format('YYYY-MM-DD')
		const daySchedules = ScheduleData?.filter((schedule: Schedule) => {
			const start = dayjs(schedule.startDate).format('YYYY-MM-DD')
			const end = dayjs(schedule.endDate).format('YYYY-MM-DD')
			return dateStr >= start && dateStr <= end
		}) || []

		return (
			<div style={{ minHeight: '60px' }}>
				{daySchedules.slice(0, 2).map((schedule: Schedule) => (
					<div
						key={schedule.id}
						style={{
							backgroundColor: schedule.color || '#3788d8',
							color: '#fff',
							padding: '2px 4px',
							margin: '1px 0',
							borderRadius: '2px',
							fontSize: '11px',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							cursor: 'pointer',
						}}
						onClick={(e) => {
							e.stopPropagation()
							handleEditSchedule(schedule)
						}}>
						{schedule.title}
					</div>
				))}
				{daySchedules.length > 2 && (
					<div style={{ fontSize: '11px', color: '#666', padding: '2px 4px' }}>
						+{daySchedules.length - 2}개 더
					</div>
				)}
			</div>
		)
	}

	const getSelectedDateSchedules = () => {
		const dateStr = selectedDate.format('YYYY-MM-DD')
		return (
			ScheduleData?.filter((schedule: Schedule) => {
				const start = dayjs(schedule.startDate).format('YYYY-MM-DD')
				const end = dayjs(schedule.endDate).format('YYYY-MM-DD')
				return dateStr >= start && dateStr <= end
			}) || []
		)
	}

	return (
		<ScheduleWrapper>
			<ScheduleHeader>
				<h2>월간 일정표</h2>
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					<Button
						onClick={() => {
							setSelectedDate(dayjs())
						}}>
						오늘
					</Button>
					<AddButton onClick={handleAddSchedule}>
						<FaPlus />
						일정 추가
					</AddButton>
				</div>
			</ScheduleHeader>
			<CalendarContainer>
				<ConfigProvider locale={locale}>
					<Calendar
						value={selectedDate}
						onSelect={handleDateSelect}
						dateCellRender={dateCellRender}
					headerRender={({ value, type, onChange, onTypeChange }) => {
						const start = 0
						const end = 12
						const monthOptions: JSX.Element[] = []
						const current = value.clone()
						const months: string[] = []
						for (let i = 0; i < 12; i++) {
							current.month(i)
							months.push(current.format('MMM'))
						}

							for (let i = start; i < end; i++) {
								monthOptions.push(
									<option key={i} value={i}>
										{months[i]}
									</option>
								)
							}

						const year = value.year()
						const month = value.month()
						const options: JSX.Element[] = []
							for (let i = year - 10; i < year + 10; i += 1) {
								options.push(
									<option key={i} value={i}>
										{i}
									</option>
								)
							}

							return (
								<div
									style={{
										padding: '8px',
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										gap: '8px',
									}}>
									<Button
										size="small"
										onClick={() => {
											const newValue = value.clone().subtract(1, 'month')
											setSelectedDate(newValue)
											onChange(newValue)
										}}>
										이전월
									</Button>
									<div style={{ display: 'flex', gap: '8px' }}>
										<select
											value={year}
											onChange={(e) => {
												const newValue = value.clone().year(Number(e.target.value))
												setSelectedDate(newValue)
												onChange(newValue)
											}}
											style={{ padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
											{options}
										</select>
										<select
											value={month}
											onChange={(e) => {
												const newValue = value.clone().month(Number(e.target.value))
												setSelectedDate(newValue)
												onChange(newValue)
											}}
											style={{ padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
											{monthOptions}
										</select>
									</div>
									<Button
										size="small"
										onClick={() => {
											const newValue = value.clone().add(1, 'month')
											setSelectedDate(newValue)
											onChange(newValue)
										}}>
										다음월
									</Button>
								</div>
							)
						}}
					/>
				</ConfigProvider>
			</CalendarContainer>
			<ScheduleList>
				<h3>{selectedDate.format('YYYY년 MM월 DD일')} 일정</h3>
				{getSelectedDateSchedules().length === 0 ? (
					<p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>선택한 날짜에 일정이 없습니다.</p>
				) : (
					getSelectedDateSchedules().map((schedule: Schedule) => (
						<ScheduleItem key={schedule.id} color={schedule.color || '#3788d8'}>
							<div className="schedule-content">
								<h4>{schedule.title}</h4>
								{schedule.content && <p>{schedule.content}</p>}
								<div className="schedule-meta">
									<span>
										{dayjs(schedule.startDate).format('YYYY-MM-DD HH:mm')} ~{' '}
										{dayjs(schedule.endDate).format('YYYY-MM-DD HH:mm')}
									</span>
									{schedule.userName && <span>작성자: {schedule.userName}</span>}
								</div>
							</div>
							<div className="schedule-actions">
								<button onClick={() => handleEditSchedule(schedule)}>
									<FaEdit />
								</button>
								<button onClick={() => handleDeleteSchedule(schedule.id)}>
									<FaTrash />
								</button>
							</div>
						</ScheduleItem>
					))
				)}
			</ScheduleList>

			<Modal
				title={isEditMode ? '일정 수정' : '일정 추가'}
				open={isModalOpen}
				onCancel={() => {
					setIsModalOpen(false)
					setEditingSchedule(null)
					setIsEditMode(false)
				}}
				onOk={handleSubmit}
				okText="저장"
				cancelText="취소"
				footer={[
					isEditMode ? (
						<Button key="delete" danger onClick={handleDeleteFromModal}>
							삭제
						</Button>
					) : null,
					<Button
						key="cancel"
						onClick={() => {
							setIsModalOpen(false)
							setEditingSchedule(null)
							setIsEditMode(false)
						}}>
						취소
					</Button>,
					<Button key="ok" type="primary" onClick={handleSubmit}>
						저장
					</Button>,
				].filter(Boolean)}
				width={600}>
				<ModalContent>
					<Form form={form} layout="vertical">
						<Form.Item
							name="title"
							label="제목"
							rules={[{ required: true, message: '제목을 입력해주세요' }]}>
							<Input placeholder="일정 제목을 입력하세요" />
						</Form.Item>
						<Form.Item name="content" label="내용">
							<Input.TextArea rows={4} placeholder="일정 내용을 입력하세요" />
						</Form.Item>
						<Form.Item
							name="startDate"
							label="시작일시"
							rules={[{ required: true, message: '시작일을 선택해주세요' }]}>
							<DatePicker
								format="YYYY-MM-DD"
								style={{ width: '100%' }}
								onChange={(v) => {
									// Auto-fill endDate if empty
									const currentEnd = form.getFieldValue('endDate')
									if (!currentEnd && v) {
										form.setFieldsValue({ endDate: dayjs(v).endOf('day') })
									}
								}}
							/>
						</Form.Item>
						<Form.Item
							name="endDate"
							label="종료일시 (선택)"
							rules={[]}>
							<DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
						</Form.Item>
						<Form.Item label="색상">
							<ColorPicker
								showText
								format="hex"
								value={selectedColor}
								onChange={(_color, hex) => {
									// `hex` is a string like '#RRGGBB'
									setSelectedColor(hex || '#3788d8')
								}}
							/>
						</Form.Item>
					</Form>
				</ModalContent>
			</Modal>
		</ScheduleWrapper>
	)
}

export default React.memo(Schedule)

